import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import TenantSuspendedPage from "../pages/TenantSuspendedPage";

/**
 * Checks tenant status before rendering children.
 * Shows suspended/trial-expired pages when appropriate.
 * Uses useLocation() to re-evaluate on every route change.
 */
const TenantStatusWrapper = ({ tenantSubdomain, children }) => {
  const location = useLocation();
  const [tenantStatus, setTenantStatus] = useState(null);
  const [tenantName, setTenantName] = useState('');
  const [tenantSource, setTenantSource] = useState('');
  const [loading, setLoading] = useState(true);
  const [trialExpired, setTrialExpired] = useState(false);
  const [overCap, setOverCap] = useState(false);

  useEffect(() => {
    const checkTenantStatus = async () => {
      if (!tenantSubdomain) { setLoading(false); return; }
      // Reset derived gating flags before each fetch so a stale over-cap /
      // trial-expired state can't keep a tenant gated after they upgrade or
      // reactivate and navigate away from /billing.
      setTrialExpired(false);
      setOverCap(false);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/tenant-info/${tenantSubdomain}`
        );
        if (response.ok) {
          const data = await response.json();
          setTenantStatus(data.status);
          setTenantName(data.company_name);
          setTenantSource(data.source || '');
          if (data.status === 'trial' && data.trial_ends_at) {
            if (new Date() > new Date(data.trial_ends_at)) setTrialExpired(true);
          }
          setOverCap(!!data.over_customer_cap);
        }
      } catch (err) {
        console.error('Error checking tenant status:', err);
      } finally {
        setLoading(false);
      }
    };
    checkTenantStatus();
    // Re-check on every route change (location.pathname) so gating reflects the
    // latest state right after an upgrade, not just once per subdomain.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSubdomain, location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (tenantStatus === 'suspended') {
    // Allow billing/reactivation pages through so logged-in users can pay
    if (location.pathname.includes('/billing') || location.pathname.includes('/reactivate')) {
      return children;
    }
    return <TenantSuspendedPage tenantName={tenantName} subdomain={tenantSubdomain} reason="admin_suspended" source={tenantSource} />;
  }

  if (trialExpired) {
    if (location.pathname.includes('/billing') || location.pathname.includes('/reactivate')) {
      return children;
    }
    return <TenantSuspendedPage tenantName={tenantName} subdomain={tenantSubdomain} reason="trial_expired" source={tenantSource} />;
  }

  // Pricing v2 — a Free tenant that has grown past 20 active subscription
  // customers (paid channels fulfill past the cap) must upgrade to Pro to keep
  // using the dashboard. Billing/reactivation stay reachable so they can.
  if (overCap) {
    if (location.pathname.includes('/billing') || location.pathname.includes('/reactivate')) {
      return children;
    }
    return <TenantSuspendedPage tenantName={tenantName} subdomain={tenantSubdomain} reason="over_customer_cap" source={tenantSource} />;
  }

  return children;
};

export default TenantStatusWrapper;
